import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  IconCalendarWeekFilled,
  IconClock,
  IconRefresh,
} from "@tabler/icons-react";
import MDialog from "@/components/ui/atoms/MDialog";
import MInputField from "@/components/ui/atoms/MInputField";
import { Reminders } from "@/types/reminders";
import { getEmphasizedText } from "./remindersHelper";
import { formatTimeWithTimezone } from "@/utils/date-utils";
import styles from "./ReminderSummary.module.css";

export interface ReminderViewModalProps {
  reminder: Reminders | null;
  isOpen: boolean;
  onClose: () => void;
}

// Read-only "view" modal shown when tapping a reminder on the listing
// screen (Figma 4904-114586) — recap card (no type icon, per that spec —
// unlike the listing row), date, dose/reminder times, and notes, all
// display-only. Editing is a separate action (the listing's existing
// 3-dot "Edit" menu), which opens the full edit screen instead.
const ReminderViewModal: React.FC<ReminderViewModalProps> = ({
  reminder,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!reminder) return null;

  // Same type-aware summary line shown on the listing card itself, so the
  // modal's subtitle is never blank/wrong for non-Vitals reminder types.
  const frequencyText = getEmphasizedText(reminder, t);

  // Multiple-times-a-day reminders (medication, vitals) carry their times in
  // dayFrequencyValue; everything else fires once, at reminder.startDate —
  // fall back to that single time so every type gets a "Reminder time"
  // section instead of just the ones with an explicit dose schedule.
  const timeSlots =
    reminder.dayFrequencyValue && reminder.dayFrequencyValue.length > 0
      ? reminder.dayFrequencyValue
      : reminder.startDate
      ? [formatTimeWithTimezone(reminder.startDate, reminder.timezone)]
      : [];

  const title = reminder.name
    ? t("reminders.viewModal.titleFor", { name: reminder.name })
    : t("reminders.viewModal.titleOwn");

  return (
    <MDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      titleVariant="sm"
      showPrimaryCta={false}
    >
      <div className="flex flex-col gap-6 w-full">
        <div className={styles.recapCard}>
          <div className={styles.recapText}>
            <span className={styles.recapLabel}>{reminder.reminderFor}</span>
            {frequencyText && (
              <span className={styles.recapFrequency}>
                <IconRefresh size={16} className={styles.recapFrequencyIcon} />
                {frequencyText}
              </span>
            )}
          </div>
        </div>

        {reminder.startDate && (
          <MInputField
            label={t("reminders.viewModal.reminderDateLabel")}
            value={format(new Date(reminder.startDate), "EEE, dd MMM yyyy")}
            variant="SummaryDisplay"
            showIcon
            icon={<IconCalendarWeekFilled />}
            readOnly
          />
        )}

        {timeSlots.length > 0 && (
          <div className={styles.timeSlotLabelWrap}>
            <span className={styles.timeSlotLabel}>
              {t("reminders.reminderTimesLabel")}
            </span>
            <div className={styles.timeSlotBox}>
              <div className={styles.timeSlotRow}>
                {timeSlots.map((time, index) => (
                  <div
                    key={`${time}-${index}`}
                    className={`${styles.timeChip} ${styles.timeChipReadOnly}`}
                  >
                    <span className={styles.timeChipValue}>{time}</span>
                    <IconClock size={16} className={styles.timeChipClock} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {reminder.notes && (
          <div className="flex flex-col gap-3 w-full">
            <span className={styles.timeSlotLabel}>
              {t("reminders.viewModal.detailsLabel")}
            </span>
            <div className="border border-[#C8B7E0] rounded-xl px-1.5 py-[15px] w-full min-h-[96px] box-border">
              <p className="text-[12px] font-medium text-[#36116D] tracking-[0.12px] m-0">
                {reminder.notes}
              </p>
            </div>
          </div>
        )}
      </div>
    </MDialog>
  );
};

export default ReminderViewModal;
